'use client'

/**
 * Notation Guide — public /floggers wizard.
 *
 * The legacy "Learn the Notation" block stacked four dense sections
 * (knot chips, formula legend, build-plan walkthrough, rope-consumption
 * tables) on the floggers index page and pushed all the actual content
 * below the fold. This component packages the same content as a
 * collapsed-by-default wizard:
 *
 * 1. Default state — just the intro line + "Open the guide" CTA.
 *    Designs & knot grids stay close to the top of the page.
 * 2. Expanded — four-step stepper with Prev/Next + clickable tabs.
 *    State lives client-side (no URL hash) since the guide is reference
 *    material, not a flow with shareable progress.
 *
 * Static reference data (knot chips, consumption tables) lives in
 * `notation-guide-data.ts` so this file stays focused on layout.
 */

import { useState } from 'react'
import { Link } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, GraduationCap, FlaskConical } from 'lucide-react'
import {
  NOTATION_KNOTS,
  KNOT_CONSUMPTION,
  HALF_HITCH_HANDLE,
  CROWN_SINNET,
  ROPE_CALCULATOR_DOWNLOAD_URL,
} from './notation-guide-data'

type T = ReturnType<typeof useTranslations<'floggersList'>>

interface Props {
  /** Slugs of currently-published Sanity knot docs — used to validate
   *  chip links so we don't render hrefs that 404. Built on the server
   *  and passed in as a plain string array (Sets aren't serializable
   *  across the server→client boundary). */
  publishedSlugs: string[]
}

const STEP_KEYS = ['knots', 'formula', 'walkthrough', 'consumption'] as const
type StepKey = (typeof STEP_KEYS)[number]

export function NotationGuide({ publishedSlugs }: Props) {
  const t = useTranslations('floggersList')
  const [open, setOpen] = useState(false)

  return (
    <section className="mt-12 border border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30 overflow-hidden">
      {/* ─── Header ────────────────────────────────────────────────── */}
      <div className="p-6 sm:p-8">
        {/* Intro — plain text; the actions below are the two clear choices. */}
        <p className="text-xs font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-400">
          {t('notation.subheading')}
        </p>
        <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
          {t('notation.heading')}
        </h2>
        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          {t('notation.guideTeaser')}
        </p>

        {/* Two options: open/close the guide, OR take the course. */}
        <div className="mt-5 space-y-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="w-full flex items-center justify-between gap-4 bg-brand-700 px-4 py-3 text-sm font-semibold text-white hover:bg-brand-800 transition-colors"
          >
            <span>{open ? t('notation.guideClose') : t('notation.guideOpen')}</span>
            {open ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>

          {/* or */}
          <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
            {t('notation.or')}
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
          </div>

          <Link
            href="/courses/building-your-first-flogger"
            className="group flex items-center gap-4 border border-brand-200 dark:border-brand-900/60 bg-brand-50/70 dark:bg-brand-900/20 px-4 py-3 hover:border-brand-400 hover:bg-brand-100/60 dark:hover:bg-brand-900/30 transition-colors"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-brand-700 text-white">
              <GraduationCap className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t('notation.courseCtaTitle')}
              </p>
              <p className="mt-0.5 text-xs text-gray-700 dark:text-gray-300">
                {t('notation.courseCtaBody')}
              </p>
            </div>
            <span className="shrink-0 text-sm font-semibold text-brand-700 dark:text-brand-400 group-hover:text-brand-800">
              {t('notation.courseCtaAction')} →
            </span>
          </Link>
        </div>
      </div>

      {/* ─── Wizard body ───────────────────────────────────────────── */}
      {open && (
        <div className="border-t border-gray-200 dark:border-gray-800 px-6 sm:px-8 pb-8 pt-6">
          <NotationGuideSteps publishedSlugs={publishedSlugs} onDone={() => setOpen(false)} />
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  The 4-step wizard itself — standalone so it can be embedded both   */
/*  in the /floggers collapsible guide (above) AND as a tab inside     */
/*  the FloggerMediaTabs box on every flogger detail page.             */
/* ------------------------------------------------------------------ */

export function NotationGuideSteps({
  publishedSlugs,
  onDone,
}: {
  publishedSlugs: string[]
  /** When provided, the last step shows a "Done" button that calls this
   *  (the /floggers guide uses it to collapse). Omitted in the tab context. */
  onDone?: () => void
}) {
  const t = useTranslations('floggersList')
  const [step, setStep] = useState<StepKey>('knots')
  const publishedSet = new Set(publishedSlugs)

  const stepIndex = STEP_KEYS.indexOf(step)
  const isFirst = stepIndex === 0
  const isLast = stepIndex === STEP_KEYS.length - 1

  return (
    <div>
      {/* Stepper tabs */}
      <ol className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800 pb-3">
        {STEP_KEYS.map((key, idx) => {
          const isActive = key === step
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => setStep(key)}
                className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center text-[10px] font-bold ${
                    isActive
                      ? 'bg-white text-brand-700'
                      : 'bg-brand-100 dark:bg-brand-900/50 text-brand-700 dark:text-brand-300'
                  }`}
                >
                  {idx + 1}
                </span>
                {t(`notation.step.${key}.label`)}
              </button>
            </li>
          )
        })}
      </ol>

      {/* Step content */}
      <div className="mt-6 min-h-[280px]">
        {step === 'knots' && <KnotsStep publishedSlugs={publishedSet} t={t} />}
        {step === 'formula' && <FormulaStep t={t} />}
        {step === 'walkthrough' && <WalkthroughStep t={t} />}
        {step === 'consumption' && <ConsumptionStep t={t} />}
      </div>

      {/* Prev / Next nav */}
      <div className="mt-8 flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-4">
        <button
          type="button"
          disabled={isFirst}
          onClick={() => !isFirst && setStep(STEP_KEYS[stepIndex - 1])}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-700 disabled:text-gray-300 dark:disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('notation.prev')}
        </button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {stepIndex + 1} / {STEP_KEYS.length}
        </span>
        {isLast ? (
          onDone ? (
            <button
              type="button"
              onClick={onDone}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
            >
              {t('notation.finish')}
            </button>
          ) : (
            <span aria-hidden className="w-12" />
          )
        ) : (
          <button
            type="button"
            onClick={() => setStep(STEP_KEYS[stepIndex + 1])}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-700 hover:text-brand-800 transition-colors"
          >
            {t('notation.next')}
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Step 1 — Knot abbreviations                                        */
/* ------------------------------------------------------------------ */

function KnotsStep({ publishedSlugs, t }: { publishedSlugs: Set<string>; t: T }) {
  const renderRow = (
    items: ReadonlyArray<{ abbr: string; name: string; slug: string | null }>,
  ) => (
    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {items.map((item) => {
        const isLinked = item.slug && publishedSlugs.has(item.slug)
        const inner = (
          <>
            <span className="font-mono text-xs font-bold text-brand-700 dark:text-brand-400 mr-2 shrink-0">
              {item.abbr}
            </span>
            <span className="text-sm text-gray-800 dark:text-gray-200">{item.name}</span>
          </>
        )
        return (
          <li key={item.abbr}>
            {isLinked ? (
              <Link
                href={`/floggers/knots/${item.slug}`}
                className="flex items-center px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 hover:border-brand-300 hover:bg-brand-50/50 dark:hover:bg-brand-900/20 transition-colors"
              >
                {inner}
              </Link>
            ) : (
              <div
                className="flex items-center px-3 py-2 border border-dashed border-gray-200 dark:border-gray-700 text-gray-400"
                title="Not yet published"
              >
                {inner}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )

  return (
    <div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {t('notation.intro')}
      </p>
      <div className="mt-6 space-y-6">
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('notation.fallsHeading')}
          </h3>
          {renderRow(NOTATION_KNOTS.falls)}
        </div>
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('notation.transitionsHeading')}
          </h3>
          {renderRow(NOTATION_KNOTS.transitions)}
        </div>
        <div>
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('notation.handlesHeading')}
          </h3>
          {renderRow(NOTATION_KNOTS.handles)}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Step 2 — Reading the formula                                       */
/* ------------------------------------------------------------------ */

function FormulaStep({ t }: { t: T }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('notation.legendHeading')}
        </h3>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">6s</dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendStrands')}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">
              <span className="font-bold">bold</span>
            </dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendBold')}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">&lt; &gt;</dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendArrows')}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">
              <span className="italic">italic</span>
            </dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendItalic')}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">F#[ ]</dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendFalls')}</dd>
          </div>
          <div className="flex gap-3">
            <dt className="font-mono font-bold text-brand-700 dark:text-brand-400 shrink-0 w-12">(N)</dt>
            <dd className="text-gray-700 dark:text-gray-300">{t('notation.legendRepeat')}</dd>
          </div>
        </dl>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('notation.exampleHeading')}
        </h3>
        <pre className="mt-3 p-4 bg-gray-900 text-white font-mono text-sm overflow-x-auto whitespace-pre-wrap">
          {`6s – 2FRK6 < `}
          <span className="italic">HHH</span>
          {` < `}
          <span className="font-bold">MWK6</span>
          {` > F6[ (3)BK2 ]`}
        </pre>
        <p className="mt-3 text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
          {t('notation.exampleCaption')}
        </p>
        {/* Kara's Rebuke build diagram — hosted in Sanity (this guide is
            otherwise hardcoded reference content, so a direct CDN URL is fine).
            ?auto=format serves webp; width/height reserve the 16:9 aspect. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="https://cdn.sanity.io/images/vedbawa9/production/df2a4f9c1c9e5f8f8c1786bad31499725d9c0f14-960x540.jpg?auto=format&fit=max"
          alt={t('notation.exampleImageAlt')}
          width={960}
          height={540}
          className="mt-4 w-full h-auto rounded border border-gray-200 dark:border-gray-700 bg-white"
        />
        <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-400">
          {t('notation.exampleTutorialNote')}
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Step 3 — Build plan walkthrough                                    */
/* ------------------------------------------------------------------ */

function WalkthroughStep({ t }: { t: T }) {
  return (
    <div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {t('notation.walkthroughIntro')}
      </p>
      <ol className="mt-5 space-y-3">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <li
            key={n}
            className="flex gap-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 p-4"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-brand-100 dark:bg-brand-900/40 text-xs font-bold text-brand-700 dark:text-brand-300">
              {n}
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {t(`notation.walkthroughStep${n}Title`)}
              </p>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {t(`notation.walkthroughStep${n}Body`)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Step 4 — Rope consumption                                          */
/* ------------------------------------------------------------------ */

function ConsumptionStep({ t }: { t: T }) {
  return (
    <div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {t('notation.consumptionIntro')}
      </p>

      {/* Ready-to-go calculator download — deliberately ABOVE the tables:
          the spreadsheet does the arithmetic these reference tables feed,
          so it's the shortcut most visitors actually want. Served from
          Sanity's asset store (see ROPE_CALCULATOR_DOWNLOAD_URL). */}
      <div className="mt-4 flex flex-col gap-3 border border-brand-200 bg-brand-50/60 p-4 sm:flex-row sm:items-center sm:justify-between dark:border-brand-800 dark:bg-brand-950/30">
        <div className="flex items-start gap-3">
          <Download className="mt-0.5 h-5 w-5 shrink-0 text-brand-700 dark:text-brand-400" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('notation.calculatorTitle')}
            </p>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
              {t('notation.calculatorBody')}
            </p>
          </div>
        </div>
        <a
          href={ROPE_CALCULATOR_DOWNLOAD_URL}
          className="inline-flex shrink-0 items-center gap-1.5 bg-brand-700 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-brand-800"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t('notation.calculatorCta')}
        </a>
      </div>

      {/* Interactive version — an experimental in-browser calculator built from
          the same spreadsheet. Text is intentionally English-only while the
          feature is experimental; wire it to `t()` if it graduates. */}
      <Link
        href="/floggers/rope-calculator"
        className="mt-2 inline-flex flex-wrap items-center gap-2 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300"
      >
        <span className="inline-flex items-center gap-1.5">
          <FlaskConical className="h-3.5 w-3.5" aria-hidden />
          Try the interactive calculator
        </span>
        <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-300">
          Experimental
        </span>
      </Link>

      {/* Knot consumption table */}
      <div className="mt-6">
        <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
          {t('notation.consumptionTableHeading')}
        </h4>
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-semibold">{t('notation.consumptionColKnot')}</th>
                <th className="px-3 py-2 font-semibold">{t('notation.consumptionColAbbr')}</th>
                <th className="px-3 py-2 font-semibold">{t('notation.consumptionColPerStrand')}</th>
                <th className="px-3 py-2 font-semibold">{t('notation.consumptionColHeight')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900/40">
              {KNOT_CONSUMPTION.map((row) => (
                <tr key={row.abbr} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{row.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-700 dark:text-brand-400">
                    {row.abbr}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {row.perStrandIn}″ <span className="text-xs text-gray-500">({row.perStrandCm} cm)</span>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {row.heightIn}″ <span className="text-xs text-gray-500">({row.heightCm} cm)</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sub-tables */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('notation.consumptionHandleHeading')}
          </h4>
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionHandleColDiameter')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionHandleColPerInch')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionHandleColWraps')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionHandleColForSeven')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900/40">
                {HALF_HITCH_HANDLE.map((row) => (
                  <tr key={row.diameter} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200">{row.diameter}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.perInch}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.wraps}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.forSeven}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('notation.consumptionSinnetHeading')}
          </h4>
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 dark:bg-gray-800/60 text-left text-xs uppercase tracking-wider text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionSinnetColStrands')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionSinnetColPerInch')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionSinnetColForSeven')}</th>
                  <th className="px-3 py-2 font-semibold">{t('notation.consumptionSinnetColWidth')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900/40">
                {CROWN_SINNET.map((row) => (
                  <tr key={row.label} className="hover:bg-gray-50 dark:hover:bg-gray-900/50">
                    <td className="px-3 py-2 font-mono text-gray-800 dark:text-gray-200">{row.label}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.perInch}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.forSeven}</td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{row.width}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  )
}
